import { cn } from '@invent/wl-ui-kit-next';
import { Tooltip, TooltipContent, TooltipTrigger } from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { RowOpens } from '@/design';

import type { IDataColumn } from '@/design';
import type { IListField as ICatalogueField, IListFieldCatalogue } from '../../api/lists-contracts';
import { secAdvApi } from '../../api/sec-adv-api';
import type {
  IAdminSecAdvFirmDetail,
  IAdminSecAdvFirmRow,
  ISecAdvCatalogue
} from '../../api/sec-adv-contracts';
import { firmCardPath } from '../../components/firm-link';
import { recordPath } from '../../components/participant-link';
import { TEAM_PATHS } from '../../paths';
import type { IOfferedFields } from '../lists/work-page/columns';
import { FirmHeader, type IColumnFunnel } from './firm-filters';
import { KIND_ICON, LIST_FIELDS, NAME_FIELD, rawField, rawFields, type IListField } from './list-fields';
import { Dash, SourceLink } from './register';

/**
 * The firm list's columns as the table draws them (SR-423): a list field or a catalogue attribute
 * as a column — its header with the sort and the funnel, its cells linked to their answers — and
 * the attributes the Columns sheet offers beside the list fields.
 */

/** The frozen name column's cell and header: stuck beside the row's icons (`openColumn`) at the frame's left
 *  edge, on the card's colour, with a hairline at its right. */
const FROZEN = 'sticky left-14 z-[1] bg-card shadow-[inset_-1px_0_0_var(--border)]';

/** Both ways in, first on a row: the firm's card beside the register (`beside`) and in a new tab —
 *  frozen at the frame's left edge, its width the name's offset. The column is none the viewer
 *  arranges. */
export function openColumn(beside: (firm: IAdminSecAdvFirmRow) => void): IDataColumn<IAdminSecAdvFirmRow> {
  return {
    id: 'open',
    header: <span className="sr-only">Open</span>,
    label: 'Open',
    className: 'sticky left-0 z-[1] w-14 min-w-14 max-w-14 bg-card whitespace-nowrap',
    headClassName: 'left-0 z-[3] w-14 min-w-14 max-w-14',
    cell: (row) => {
      const name = row.businessName ?? row.legalName;
      return (
        <RowOpens
          name={name}
          onPane={() => beside(row)}
          to={firmCardPath(row.firmCrd)}
          tabWords={`Open ${name} in a new tab`}
        />
      );
    }
  };
}

export function tableColumn(
  field: IListField,
  {
    q,
    catalogue,
    sorted = null,
    onSort = () => undefined,
    funnel
  }: {
    q: string;
    catalogue: Nullable<ISecAdvCatalogue>;
    sorted?: Nullable<'asc' | 'desc'>;
    onSort?: () => void;
    funnel?: IColumnFunnel;
  }
): IDataColumn<IAdminSecAdvFirmRow> {
  const frozen = field.key === NAME_FIELD;
  return {
    id: field.key,
    header: <FirmHeader field={field} sorted={sorted} onSort={onSort} funnel={funnel} />,
    label: field.label,
    numeric: field.numeric,
    className: cn(frozen ? cn(FROZEN, 'w-60 max-w-64 min-w-52') : 'whitespace-nowrap', field.width),
    headClassName: cn(frozen && 'left-14 z-[3] shadow-[inset_-1px_-1px_0_var(--border)]'),
    cell: (row) => {
      if (frozen) return <FirmName row={row} q={q} />;
      const value = field.render(row, { catalogue });
      if (value === null || value === undefined || value === '') return <Dash />;
      // A dash with its reason (an exempt reporting adviser's AUM) has no answer to link to, and
      // no tab stop: a cell's would make one per row.
      if (React.isValidElement<React.ComponentProps<typeof Dash>>(value) && value.type === Dash) {
        return React.cloneElement(value, { focusable: false });
      }
      return field.source && field.item ? (
        <SourceLink firmCrd={row.firmCrd} source={field.source}>
          {value}
        </SourceLink>
      ) : (
        value
      );
    }
  };
}

/** The firm as it does business, on one line, its legal name on hover — and under it where that
 *  differs and is what the search found. */
function FirmName({ row, q }: { row: IAdminSecAdvFirmRow; q: string }) {
  const name = row.businessName ?? row.legalName;
  const legalFound =
    q.trim() !== '' &&
    row.legalName !== name &&
    (row.matches ?? []).some((match) => match.field === 'legal_name');
  return (
    <>
      <Tooltip>
        <TooltipTrigger asChild>
          <Link
            to={firmCardPath(row.firmCrd)}
            className="block truncate font-medium text-[var(--color-royal-blue)] hover:underline"
          >
            {name}
          </Link>
        </TooltipTrigger>
        <TooltipContent className="max-w-sm">
          {row.legalName}
          {row.legalName !== name && <span className="block text-xs opacity-80">{name}</span>}
        </TooltipContent>
      </Tooltip>
      {legalFound && <span className="block truncate text-xs text-muted-foreground">{row.legalName}</span>}
      {row.participantCount > 0 && <OurRecords row={row} />}
    </>
  );
}

/**
 * Our records holding the firm's numbers (SR-514). The count is of records holding its SEC number
 * or its CRD, and Firms' search by the SEC number finds only the first, so a plain press reads the
 * firm's card first: one record opens it, several open Firms searched by the SEC number where one
 * holds it, else by the CRD. The address is Firms by the SEC number, so a new tab still lands
 * somewhere useful, as does a failed read. Its own link — a press never opens the firm's page.
 */
function OurRecords({ row }: { row: IAdminSecAdvFirmRow }) {
  const navigate = useNavigate();
  const [resolving, setResolving] = React.useState(false);
  const href = firmsSearched(row.secNumber);
  return (
    <Link
      to={href}
      onClick={(event) => {
        event.stopPropagation();
        const modified = event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;
        if (event.button !== 0 || modified) return;
        event.preventDefault();
        if (resolving) return;
        setResolving(true);
        secAdvApi
          .getFirm(row.firmCrd)
          .then((firm) => navigate(ourRecordsPath(row, firm) ?? href))
          .catch(() => navigate(href))
          .finally(() => setResolving(false));
      }}
      aria-busy={resolving || undefined}
      data-our-records=""
      className="block w-fit text-xs text-muted-foreground hover:text-[var(--color-royal-blue)] hover:underline aria-busy:cursor-progress"
    >
      {row.participantCount === 1 ? 'Our record' : `Our records (${row.participantCount})`}
    </Link>
  );
}

/** Firms searched for a number. */
function firmsSearched(number: string): string {
  return `${TEAM_PATHS.firms}?${new URLSearchParams({ q: number })}`;
}

/** Where the firm card's participants are: the one's record page, or Firms searched by the number
 *  they hold — null for a card with none, or none at all. */
function ourRecordsPath(
  row: IAdminSecAdvFirmRow,
  firm: Nullable<IAdminSecAdvFirmDetail>
): Nullable<string> {
  const participants = firm?.participants ?? [];
  if (participants.length === 0) return null;
  if (participants.length === 1) {
    const [only] = participants;
    return recordPath(only.participantId, { recordType: only.kind });
  }
  const bySec = participants.some((participant) => participant.matchedBy.includes('SEC_NUMBER'));
  return firmsSearched(bySec ? row.secNumber : row.firmCrd);
}

/** The catalogue's attributes as the Columns sheet offers them beside the list fields, each in its
 *  Form ADV item's group. */
export function useOfferedFields(
  catalogue: Nullable<ISecAdvCatalogue>,
  q: string
): IOfferedFields<IAdminSecAdvFirmRow> {
  const sheet = React.useMemo(() => columnCatalogue(catalogue), [catalogue]);
  const keys = React.useMemo(() => new Set(rawFields(catalogue).map((field) => field.key)), [catalogue]);
  return {
    catalogue: sheet,
    // A raw attribute is `<element path>.<attribute>`; a list field's key holds no dot.
    offers: (code) => code.includes('.') && (catalogue === null || keys.has(code)),
    column: (code) => tableColumn(rawField(catalogue, code), { q, catalogue }),
    iconOf: (code) =>
      KIND_ICON[(LIST_FIELDS.find((field) => field.key === code) ?? rawField(catalogue, code)).kind],
    max: MAX_RAW_COLUMNS
  };
}

/** The most raw attributes a read asks for — survey-api's `MaxColumns` on `columns=`. */
const MAX_RAW_COLUMNS = 100;

function columnCatalogue(catalogue: Nullable<ISecAdvCatalogue>): Nullable<IListFieldCatalogue> {
  if (catalogue === null) return null;
  const groups = new Map<string, ICatalogueField[]>();
  for (const field of rawFields(catalogue)) {
    const entry: ICatalogueField = {
      code: field.key,
      label: field.label,
      number: field.item ?? null,
      kind: 'text',
      options: null,
      column: null,
      hot: false,
      editable: false,
      editType: null,
      filterable: false,
      sortable: false,
      groupable: false,
      summable: false,
      access: 'staff',
      anomalyThreshold: null
    };
    const group = groups.get(field.group);
    if (group) group.push(entry);
    else groups.set(field.group, [entry]);
  }
  return { survey: 'sec-adv', groups: [...groups].map(([source, fields]) => ({ source, fields })) };
}
